<template>
  <div>
    <ToastPrime position="bottom-left" />

    <div class="card-body pt-0 px-0" style="max-height: 400px; overflow-y: auto;"
      :class="{ 'form-disabled': readonly }">
      <DataTable :value="isHeaderLoading ? dummyRows : rows" class="p-datatable-sm p-datatable-gridlines" scrollable
        table-style="min-width: 40rem">

        <!-- Change Master Data Type -->
        <Column v-if="isColumnVisibleS('change_md_type')" header-class="bg-light text-center"
          :header-style="{ width: '260px' }" :body-style="{ width: '260px' }">
          <template #header>Change Master Data Type <span v-if="rows.some(r => isColumnMandatoryS(r, 'change_md_type'))"
              class="text-danger">*</span></template>
          <template #body="{ data }">
            <Skeleton v-if="isHeaderLoading" />
            <SelectPrime v-else v-model="data.change_md_type" :options="optionsFor(data, 'change_md_type')"
              option-label="label" option-value="value" placeholder="Select"
              :disabled="!isColumnEnabledS(data, 'change_md_type')"
              :invalid="isColumnInvalidFn(data, 'change_md_type')" fluid
              @show="onColumnShow(data, 'change_md_type')"
              @update:model-value="v => onColumnChange(data, 'change_md_type', v)" />
          </template>
        </Column>

        <!-- Specification -->
        <Column v-if="isColumnVisibleS('specification')" header-class="bg-light text-center" style="min-width:240px">
          <template #header>Specification <span v-if="rows.some(r => isColumnMandatoryS(r, 'specification'))"
              class="text-danger">*</span></template>
          <template #body="{ data }">
            <Skeleton v-if="isHeaderLoading" />
            <InputText v-else v-model="data.specification" size="small" placeholder="Specification"
              :disabled="!isColumnEnabledS(data, 'specification')"
              :invalid="isColumnInvalidFn(data, 'specification')" fluid />
          </template>
        </Column>

        <!-- Action -->
        <Column header-class="bg-light text-center" body-class="text-center"
          :header-style="{ width: '70px', minWidth: '70px' }" :body-style="{ width: '70px', minWidth: '70px' }">
          <template #header>
            <div class="centered-column-header">#</div>
          </template>
          <template #body="{ index }">
            <Skeleton v-if="isHeaderLoading" />
            <ButtonPrime v-else icon="pi pi-trash" size="small" severity="danger" outlined rounded
              :disabled="readonly || rows.length <= 1" @click="removeRow(index)" />
          </template>
        </Column>

        <template #empty>
          <div style="text-align:center;padding:40px 0">
            <img src="/no_data.png" alt="No Data" style="max-width:330px;opacity:0.7" />
            <div style="color:#888;font-size:1.1em;font-weight:500">No Data</div>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- Dialog Tracking & Import Excel digerakkan parent (New_McrItem) lewat toolbar di sana —
         jangan pasang instance kedua di sini, dialognya jadi dobel karena state-nya sama. -->
  </div>
</template>

<script>
import { getCurrentInstance } from 'vue';
import { mapState } from 'vuex';
import formConfigHelper from '@/helpers/New_formConfig';
import useFormConfigEngine from '@/composables/useFormConfigEngine';

// Config form ini TIDAK di-request di sini: New_McrForm sudah mengisinya sekali ke Vuex
// (`mcrDetail.formConfig`, sesuai form_code FORM07), jadi form ini cukup membaca config yang
// sama dari store — termasuk data_source opsi dropdown.
//
// Tab change master data di form config. Backend bisa menulis nama tab dengan pemisah lain
// ("Change Master Data", "Change/Master/Data"), jadi key-nya dinormalkan dulu.
const CHANGE_MD_TAB = 'change_master_data';
const normalizeTabKey = (key) => String(key).toLowerCase().replace(/[^a-z0-9]+/g, '_');

// Kolom yang dirender form ini. Dipakai sebagai fallback saat config belum termuat — begitu
// config ada, daftar kolomnya diambil dari `columns` milik field repeatable di tab.
const RENDERED_COLUMNS = ['change_md_type', 'specification'];

// Key warisan FE di item store (opsi versi lama disimpan flat di row padahal sekarang wadahnya
// per kolom `row.columnOptions[column]`). Dibuang supaya tidak bentrok & tidak ikut ter-submit.
const LEGACY_ROW_KEYS = ['options', 'loading', 'loaded'];

// State visual per baris yang tidak boleh ikut ter-submit.
const VISUAL_STATE_KEYS = ['columnOptions', 'columnLoading', 'columnLoaded', 'rowErrors'];

export default {
  name: 'ChangeMasterDataForm',
  props: {
    readonly: { type: Boolean, default: false }
  },
  setup(props) {
    // `vm` dibaca lazy lewat getter -- data() baru ada setelah setup() jalan (pola McrItemFormDialog).
    const vm = getCurrentInstance().proxy;

    // Baris & kolom yang sedang dievaluasi engine. Holder ini SENGAJA non-reaktif (plain object
    // di closure setup): kalau reactive, penugasan saat render memicu re-render loop.
    const rowContext = { row: null, column: null };

    // Wadah opsi/loading/loaded per baris PER KOLOM (engine menulis ke `dialog.options[field]`
    // dengan `field` = nama field repeatable, sedangkan 1 field repeatable punya banyak kolom select).
    const bucketKey = (column) => column || '__row';

    // Jalur TULIS (dipakai getDialog()): buat wadahnya kalau belum ada.
    const storeFor = (row, column) => {
      if (!row) return { options: {}, loading: {}, loaded: {} };
      const key = bucketKey(column);
      row.columnOptions = row.columnOptions || {};
      row.columnLoading = row.columnLoading || {};
      row.columnLoaded = row.columnLoaded || {};
      row.columnOptions[key] = row.columnOptions[key] || {};
      row.columnLoading[key] = row.columnLoading[key] || {};
      row.columnLoaded[key] = row.columnLoaded[key] || {};
      return {
        options: row.columnOptions[key],
        loading: row.columnLoading[key],
        loaded: row.columnLoaded[key],
      };
    };
    // Jalur BACA (dipanggil saat render) — TIDAK menambah key apa pun.
    const readStore = (row, column) => {
      const key = bucketKey(column);
      return {
        options: row?.columnOptions?.[key] || {},
        loading: row?.columnLoading?.[key] || {},
        loaded: row?.columnLoaded?.[key] || {},
      };
    };

    const engine = useFormConfigEngine({
      vm,
      getFormConfig: () => vm.formConfig,
      // Tiap baris grid = satu "dialog" sendiri, dan tiap kolomnya satu wadah opsi sendiri.
      getDialog: () => {
        const store = storeFor(rowContext.row, rowContext.column);
        return {
          form: rowContext.row || {},
          options: store.options,
          loading: store.loading,
          loaded: store.loaded,
          errors: (rowContext.row && rowContext.row.rowErrors) || {},
        };
      },
      getMcrType: () => vm.mcrHeader?.type || '',
      isView: () => props.readonly,
      getCtx: () => vm.fieldCtxS(rowContext.row),
    });

    return { ...engine, rowContext, storeFor, readStore };
  },
  data() {
    return {
      rows: [],
      dummyRows: Array.from({ length: 3 }, () => ({})),
      formConfig: null,
      companyCode: '',
      clientCode: ''
    };
  },
  computed: {
    ...mapState({
      isHeaderLoading: state => state.mcr.isLoading.header,
      mcrHeader: state => state.mcr.mcrHeader,
      allMcrItem: state => state.mcr.allMcrItem || [],
      // Sumber config form: diisi New_McrForm sesuai form_code (bukan fetch di sini).
      mcrDetail: state => state.mcr.mcrDetail,
    }),
    tabKey() {
      return this.resolveTabKey(this.formConfig);
    },
    tabFields() {
      return formConfigHelper.getTabFieldMap(this.formConfig, this.tabKey) || null;
    },
    // Field repeatable pemilik baris grid (di FORM07: field `change_master_data` di tab `change_master_data`).
    gridField() {
      return this.resolveGridField(this.formConfig, this.tabKey);
    },
    gridFieldConfig() {
      return this.tabFields?.[this.gridField] || null;
    },
    // Daftar kolom yang dipakai form: dari config, fallback ke daftar kolom yang dirender.
    columns() {
      const columns = this.resolveColumns(this.formConfig);
      return columns.length ? columns : [...RENDERED_COLUMNS];
    },
  },
  watch: {
    // Config form datang dari Vuex (diisi New_McrForm, sudah dinormalisasi di sana). `immediate`
    // dipakai supaya config yang sudah termuat sebelum form ini mount langsung terpakai.
    'mcrDetail.formConfig': {
      immediate: true,
      handler(val) {
        this.formConfig = val || null;
      },
    },
    // Config selesai dimuat -> opsi & default tiap baris di-resolve ulang dari config.
    formConfig() {
      this.rows.forEach(row => this.refreshRow(row));
    },
    // Baris dibangun dari item MCR di store (MCR010 sudah dimapping khusus di fetchMcrHeader).
    allMcrItem: {
      immediate: true,
      handler(val) {
        const base = (val && val.length > 0) ? [...val] : [this.emptyRow()];
        this.rows = base.map(item => this.normalizeRow(item));
        this.rows.forEach(row => this.refreshRow(row));
      }
    }
  },
  mounted() {
    // company/client dipakai ctx config (data_source.params).
    const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
    this.companyCode = userAuth?.user?.company_code || '';
    this.clientCode = userAuth?.user?.client_code || '';
  },
  methods: {
    // ---------------------------------------------------------------------
    // Config
    // ---------------------------------------------------------------------
    resolveTabKey(config) {
      const keys = Object.keys(config?.tab_fields || {});
      if (keys.includes(CHANGE_MD_TAB)) return CHANGE_MD_TAB;
      const matched = keys.find(key => normalizeTabKey(key) === CHANGE_MD_TAB);
      if (matched) return matched;
      // Config FORM07 cuma punya 1 tab — kalau penamaan tab di BE beda dari dugaan,
      // pakai tab itu daripada semua kolomnya hilang.
      if (keys.length === 1) return keys[0];
      // Nama tab-nya beda jauh: pakai tab yang paling banyak mendeklarasikan kolom form ini.
      let best = CHANGE_MD_TAB;
      let bestScore = 0;
      keys.forEach(key => {
        const score = this.resolveColumnsOfTab(config, key).length;
        if (score > bestScore) {
          bestScore = score;
          best = key;
        }
      });
      return best;
    },
    // Field repeatable di 1 tab (pemilik kolom grid).
    resolveGridField(config, tabKey) {
      const fields = formConfigHelper.getTabFieldMap(config, tabKey) || {};
      if (fields[CHANGE_MD_TAB]?.columns) return CHANGE_MD_TAB;
      return Object.keys(fields).find(name => fields[name]?.columns) || CHANGE_MD_TAB;
    },
    resolveColumnsOfTab(config, tabKey) {
      const columns = formConfigHelper.getTabFieldMap(config, tabKey)?.[this.resolveGridField(config, tabKey)]?.columns;
      return columns && typeof columns === 'object' ? Object.keys(columns) : [];
    },
    resolveColumns(config) {
      return this.resolveColumnsOfTab(config, this.resolveTabKey(config));
    },
    // Context evaluator config per baris — dibaca engine lewat `getCtx`.
    fieldCtxS(row) {
      return {
        // Bikin semua kolom non-aktif & non-wajib saat form readonly.
        isView: this.readonly,
        getField: (name) => {
          if (name === 'company') return this.companyCode;
          if (name === 'client') return this.clientCode;
          if (name === 'mcr_type') return (this.mcrHeader?.type || '').toUpperCase();
          return row?.[name];
        },
        // Dipakai entry `{ param, from, attribute }` di data_source.params.
        getFieldOption: (field, attribute) => {
          const options = this.readStore(row, field).options[this.gridField] || [];
          const selected = options.find(o => String(o.value) === String(row?.[field]));
          return selected ? selected[attribute] : undefined;
        },
        hasLine: () => false,
      };
    },

    // ---------------------------------------------------------------------
    // Evaluasi config — evaluator-nya tetap dari engine (`isColumnVisibleFn`, dst).
    // Engine butuh SATU ctx, sedangkan kondisi kolom FORM07 per baris, jadi baris yang
    // sedang dievaluasi dipasang dulu ke `rowContext`.
    // ---------------------------------------------------------------------
    activate(row, column = null) {
      this.rowContext.row = row || null;
      this.rowContext.column = column;
    },
    columnConfig(column) {
      return formConfigHelper.getColumnConfig(this.formConfig, this.tabKey, this.gridField, column);
    },
    isColumnVisibleS(column) {
      // Kolom yang tidak dideklarasikan config tetap ditampilkan (config belum termuat atau
      // nama key-nya beda) — kolom baru disembunyikan kalau config memang bilang `visible` false.
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      // Kolom tampil kalau ADA baris yang memenuhi kondisi visible-nya.
      return this.rows.some(row => this.isColumnVisibleForRow(row, column));
    },
    // Visible-nya 1 kolom di BARIS tertentu (dipakai validasi & penanda error).
    isColumnVisibleForRow(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnVisibleFn(column, this.gridField, this.tabKey);
    },
    isColumnEnabledS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return true;
      this.activate(row, column);
      return this.isColumnEnabledFn(column, this.gridField, this.tabKey);
    },
    isColumnMandatoryS(row, column) {
      if (!this.gridFieldConfig || !this.columnConfig(column)) return false;
      this.activate(row, column);
      return this.isColumnMandatoryFn(column, this.gridField, this.tabKey);
    },
    // Opsi dropdown 1 kolom di 1 baris (wadah per kolom; dibaca non-creating saat render).
    optionsFor(row, column) {
      return this.readStore(row, column).options[this.gridField] || [];
    },
    // Loading state 1 kolom di 1 baris. Engine men-set-nya sendiri (per kolom) di jalur
    // data_source endpoint.
    columnLoadingS(row, column) {
      return !!this.readStore(row, column).loading[this.gridField];
    },

    // ---------------------------------------------------------------------
    // Baris
    // ---------------------------------------------------------------------
    emptyRow() {
      return {
        change_md_type: '',
        specification: '',
        // error per kolom untuk 1 baris ini (dibaca isColumnInvalidFn) + state visual
        rowErrors: {},
        columnOptions: {},
        columnLoading: {},
        columnLoaded: {},
      };
    },
    // Item dari store membawa key warisan FE — dibuang supaya tidak ikut ter-submit dan tidak
    // bentrok dengan wadah opsi yang baru.
    normalizeRow(item) {
      const row = { ...this.emptyRow(), ...item };
      LEGACY_ROW_KEYS.forEach(key => { delete row[key]; });
      // Wadah opsi selalu mulai kosong (item store tidak pernah membawa opsi yang valid).
      row.rowErrors = {};
      row.columnOptions = {};
      row.columnLoading = {};
      row.columnLoaded = {};
      return row;
    },
    columnsWithDataSource() {
      const columns = this.gridFieldConfig?.columns || {};
      return Object.entries(columns)
        .filter(([, col]) => col?.data_source)
        .map(([name]) => name);
    },
    // `depends_on` di level KOLOM (helper `isDependencyReady` cuma baca depends_on field).
    columnDependencyReady(row, column) {
      const deps = this.columnConfig(column)?.depends_on;
      if (!Array.isArray(deps) || !deps.length) return true;
      this.activate(row, column);
      const ctx = this.fieldCtxS(row);
      return deps.every(dep => formConfigHelper.isFilledValue(ctx.getField(dep)));
    },
    // Load opsi 1 kolom untuk 1 baris (data_source array / detail_array / endpoint).
    async loadColumnOptions(row, column, { force = false, search, mapItem } = {}) {
      if (!this.columnConfig(column)?.data_source) return;
      if (!this.columnDependencyReady(row, column)) return;
      this.activate(row, column);
      await this.loadSelectFieldOptions(this.gridField, {
        tab: this.tabKey,
        column,
        force,
        search,
        mapItem,
      });
    },
    // Load opsi SEMUA kolom select baris ini.
    async loadRowColumnOptions(row, { force = false } = {}) {
      const columns = this.columnsWithDataSource();
      await Promise.all(columns.map(column => this.loadColumnOptions(row, column, { force })));
    },
    // Reset kolom yang `depends_on` kolom yang berubah: opsi + nilainya dikosongkan, supaya
    // tidak ter-submit dalam keadaan disable dan opsinya ikut di-load ulang (`loaded` mati).
    resetDependentColumns(row, changedColumn) {
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        if (!Array.isArray(col?.depends_on) || !col.depends_on.includes(changedColumn)) return;
        // Ganti wadahnya: opsi lama hilang & `loaded` mati, jadi opsinya di-load ulang saat refresh.
        row.columnOptions[name] = {};
        row.columnLoading[name] = false;
        row.columnLoaded[name] = false;
        row[name] = '';
      });
    },
    // `default_value` di level KOLOM — form ini cuma memakai tipe "static" (config FORM07 belum
    // butuh lookup ke master data).
    applyColumnDefaultValues(row) {
      Object.entries(this.gridFieldConfig?.columns || {}).forEach(([name, col]) => {
        const entries = col?.default_value;
        if (!Array.isArray(entries) || !entries.length) return;
        if (!this.isColumnEnabledS(row, name)) return;

        const ctx = this.fieldCtxS(row);
        const entry = entries.find(e => !e.when || formConfigHelper.evaluateCondition(e.when, ctx));
        if (entry?.type !== 'static') return;
        if (!formConfigHelper.isFilledValue(row[name])) row[name] = entry.value;
      });
    },
    // Handler generic buat `@show` semua dropdown grid (kontrak: jangan bikin onXxxShow per kolom).
    // Opsi baris sudah dipreload saat baris dibentuk/berubah, jadi ini cuma no-op yang aman
    // (`loadSelectFieldOptions` early-return kalau sudah `loaded`).
    onColumnShow(row, column) {
      this.activate(row, column);
      this.onFieldOptionShow(this.gridField, this.tabKey, { column });
    },
    // Dipanggil setelah satu kolom di 1 baris berubah (atau saat baris dimuat).
    async refreshRow(row, changedColumn = null) {
      if (!row) return;
      this.activate(row, changedColumn);

      if (changedColumn) {
        // 1. Kolom yang `depends_on` kolom ini: opsi + nilainya direset (level kolom).
        this.resetDependentColumns(row, changedColumn);
        // 2. Nilai kolom yang jadi non-aktif dikosongkan, supaya tidak ter-submit dalam
        //    keadaan disable.
        if (!this.readonly) {
          this.columns.forEach(name => {
            if (name !== changedColumn && !this.isColumnEnabledS(row, name)) row[name] = '';
          });
        }
      }

      // Opsi kolom (array / detail_array / endpoint) diambil & disimpan per baris per kolom.
      await this.loadRowColumnOptions(row);
      // default_value level kolom (static) untuk baris ini.
      this.applyColumnDefaultValues(row);
    },
    onColumnChange(row, column, value) {
      // Nilai di-set di sini juga (bukan cuma lewat v-model) supaya urutan handler tidak berpengaruh.
      row[column] = value;
      this.refreshRow(row, column);
    },
    // Dipanggil toolbar Add di New_McrItem (nama method disamakan dengan UserForm.addNewItem).
    addNewItem() {
      const last = this.rows[this.rows.length - 1];
      if (last && !this.isRowComplete(last)) {
        this.$toast?.add({ severity: 'warn', summary: 'Incomplete Data', detail: `Please complete row ${this.rows.length} before adding a new row.`, life: 2500 });
        return;
      }
      this.rows.push(this.emptyRow());
      // Ambil balik dari `rows` supaya yang dipakai adalah PROXY reaktif-nya, bukan objek mentah —
      // kalau mentah, penulisan opsi oleh engine tidak memicu re-render (dropdown tetap kosong).
      this.refreshRow(this.rows[this.rows.length - 1]);
    },
    removeRow(index) {
      if (this.rows.length <= 1) return;
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          this.rows.splice(index, 1);
        }
      });
    },

    // ---------------------------------------------------------------------
    // Validasi & payload
    // ---------------------------------------------------------------------
    // Kolom wajib (visible + enabled) yang masih kosong di 1 baris — cuma dihitung, belum ditulis.
    rowErrorsOf(row) {
      const errors = {};
      this.columns.forEach(column => {
        if (!this.isColumnVisibleForRow(row, column)) return;
        if (!this.isColumnMandatoryS(row, column)) return;
        if (formConfigHelper.isFilledValue(row[column])) return;
        errors[column] = true;
      });
      return errors;
    },
    isRowComplete(row) {
      return Object.keys(this.rowErrorsOf(row)).length === 0;
    },
    validate() {
      // row_rules "min_rows" di level field repeatable.
      const minRows = formConfigHelper.getRowRuleValue(this.formConfig, this.tabKey, this.gridField, 'min_rows');
      let valid = !(typeof minRows === 'number' && this.rows.length < minRows);
      // Error disimpan PER BARIS (`row.rowErrors`) — konvensi yang sama dengan McrItemFormDialog.
      // Template membacanya lewat `isColumnInvalidFn` (engine), jadi tanda merah hilang sendiri
      // begitu kolomnya diisi, tanpa perlu submit ulang.
      this.rows.forEach(row => {
        row.rowErrors = this.rowErrorsOf(row);
        if (Object.keys(row.rowErrors).length) valid = false;
      });
      return valid;
    },
    // Payload untuk dikirim ke BE: `id` & `mcr_item_code` tetap ikut saat tersedia (mode edit)
    // supaya backend bisa update baris emcr yang tepat.
    getPayload() {
      return this.rows.map(row => {
        const payload = { ...row };
        // buang state visual (opsi/loading per kolom)
        VISUAL_STATE_KEYS.forEach(key => { delete payload[key]; });
        // Kolom yang tidak visible di config tidak ikut dikirim.
        this.columns.forEach(column => {
          if (!this.isColumnVisibleS(column)) delete payload[column];
        });
        return payload;
      });
    },
  }
};
</script>

<style scoped>
:deep(.centered-column-header) {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

:deep(.p-inputtext:disabled),
:deep(textarea:disabled),
:deep(.p-select.p-disabled),
:deep(.p-disabled) {
  background-color: #f5f5f5 !important;
  pointer-events: none;
  cursor: not-allowed !important;
}

:deep(.p-datatable-sm .p-datatable-tbody > tr > td) {
  font-size: 0.875rem !important;
  padding: 6px 0.75rem !important;
}

:deep(.p-datatable .p-datatable-thead > tr > th) {
  white-space: nowrap !important;
}

:deep(.p-datatable .p-datatable-tbody > tr.p-datatable-empty-message > td) {
  border: none !important;
  padding: 0 !important;
}

:deep(.p-select:not(.p-select-editable)) {
  min-height: 38px !important;
  height: 38px !important;
}

:deep(.p-select-sm:not(.p-select-editable) .p-select-label),
:deep(.p-select:not(.p-select-editable) .p-select-label) {
  font-size: 0.875rem !important;
}

:deep(.p-inputtext) {
  font-size: 0.875rem !important;
}
</style>
